import { useRef, useEffect, useCallback, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ToggleGroup, ToggleGroupItem } from "@sportscanner/ui";
import type { DateEntry } from "./helpers";

interface Props {
  dates: DateEntry[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
}

export default function DateSlider({
  dates,
  selectedDate,
  onSelectDate,
}: Props) {
  const { t } = useTranslation();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = useCallback(() => {
    const el = sliderRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 2);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const slider = sliderRef.current;
    if (!slider) return;
    slider.addEventListener("scroll", updateScrollButtons);
    const ro = new ResizeObserver(updateScrollButtons);
    ro.observe(slider);
    return () => {
      slider.removeEventListener("scroll", updateScrollButtons);
      ro.disconnect();
    };
  }, [updateScrollButtons]);

  const scroll = (dir: "left" | "right") => {
    sliderRef.current?.scrollBy({
      left: dir === "left" ? -200 : 200,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative min-w-0">
      {canScrollLeft && (
        <button
          onClick={() => scroll("left")}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white/90 shadow-md rounded-full hover:bg-white transition-colors cursor-pointer"
        >
          <ChevronLeft className="w-4 h-4 text-neutral-600" />
        </button>
      )}
      <div
        ref={sliderRef}
        className="flex gap-2 overflow-x-auto scroll-smooth px-1"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <ToggleGroup
          type="single"
          value={selectedDate}
          onValueChange={(v: string) => v && onSelectDate(v)}
          className="flex gap-2 flex-nowrap"
        >
          {dates.map((d) => (
            <ToggleGroupItem
              key={d.fullDate}
              value={d.fullDate}
              className="shrink-0 flex flex-col items-center justify-center w-14 h-[4.25rem] rounded-lg border border-neutral-200 cursor-pointer data-[state=off]:bg-white data-[state=off]:text-neutral-600 data-[state=on]:bg-primary-500 data-[state=on]:text-white data-[state=on]:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <span className="text-xs opacity-70">
                {t(`days.short.${d.dayKey}`)}
              </span>
              <span className="text-base font-bold">{d.date}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      {canScrollRight && (
        <button
          onClick={() => scroll("right")}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white/90 shadow-md rounded-full hover:bg-white transition-colors cursor-pointer"
        >
          <ChevronRight className="w-4 h-4 text-neutral-600" />
        </button>
      )}
    </div>
  );
}
